// src/layouts/PlatformDashboardLayout.js
//
// MainLayout.js's own Super Admin panel equivalent — deliberately much
// smaller: platform_routes.go itself "starts deliberately small" (see that
// file's own doc comment) and today only really has one screen family
// (Companies) plus this feature's own plan doc's still-pending Phase 6
// items (#198 — Platform users/groups/settings/notifications, not built
// yet). No i18n, no NotificationBell, no theme-color CSS variable — none
// of those exist on the platform side of this codebase at all (see
// PlatformLogin.css's own doc comment on why). Grows one nav link at a
// time exactly the way MainLayout.js's own sidebar did, as each further
// Platform screen lands.
import { useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { PlatformAuthContext } from "../context/PlatformAuthContext";
import { getPlatformUnreadNotificationCount } from "../services/api";
import "./PlatformDashboardLayout.css";

export default function PlatformDashboardLayout() {
  const { platformUser, permissions, token, logout } = useContext(PlatformAuthContext);
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // Polled rather than pushed over a websocket (unlike the tenant app's own
  // NotificationBell/useNotificationsWS) — the Platform panel has no
  // equivalent /ws endpoint of its own yet (see this feature's own plan
  // doc's Phase 6 item list, which never named one), and a small staff
  // panel's notification volume makes a 30s poll indistinguishable from
  // real-time in practice.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const poll = () => {
      getPlatformUnreadNotificationCount(token)
        .then((res) => { if (!cancelled) setUnreadCount(res.count || 0); })
        .catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  const handleLogout = async () => {
    await logout();
    navigate("/platform-login", { replace: true });
  };

  const initials = platformUser
    ? `${(platformUser.first_name || "?")[0]}${(platformUser.last_name || "")[0] || ""}`.toUpperCase()
    : "?";

  return (
    <div className="platform-shell">
      <aside className="platform-sidebar">
        <div className="platform-sidebar-brand">
          <span className="platform-sidebar-badge">Super Admin</span>
          <span className="platform-sidebar-title">Posflect Platform</span>
        </div>

        <nav className="platform-sidebar-nav">
          <NavLink
            to="/platform/companies"
            className={({ isActive }) => `platform-nav-link${isActive ? " active" : ""}`}
          >
            Companies
          </NavLink>
          {permissions.includes("platformuser_manage") && (
            <>
              <NavLink
                to="/platform/users"
                className={({ isActive }) => `platform-nav-link${isActive ? " active" : ""}`}
              >
                Platform users
              </NavLink>
              <NavLink
                to="/platform/groups"
                className={({ isActive }) => `platform-nav-link${isActive ? " active" : ""}`}
              >
                Platform groups
              </NavLink>
            </>
          )}
          {permissions.includes("platformsetting_manage") && (
            <NavLink
              to="/platform/settings"
              className={({ isActive }) => `platform-nav-link${isActive ? " active" : ""}`}
            >
              Settings
            </NavLink>
          )}
          <NavLink
            to="/platform/notifications"
            className={({ isActive }) => `platform-nav-link${isActive ? " active" : ""}`}
          >
            Notifications{unreadCount > 0 ? ` (${unreadCount})` : ""}
          </NavLink>
        </nav>
      </aside>

      <div className="platform-main">
        <header className="platform-topbar">
          <div />
          <div className="platform-topbar-user">
            <span className="platform-topbar-avatar">{initials}</span>
            <span className="platform-topbar-name">
              {platformUser ? `${platformUser.first_name} ${platformUser.last_name}` : ""}
            </span>
            <button className="platform-topbar-logout" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </header>

        <main className="platform-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
